import { useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { Icon, type IconName } from '@luacentrix/icons';
import { cn } from './utils';

export interface TabItem {
  id: string;
  label: ReactNode;
  icon?: IconName;
  disabled?: boolean;
  badge?: ReactNode;
}

export interface TabsProps {
  items: Array<TabItem>;
  value: string;
  onChange: (next: string) => void;
  ariaLabel: string;
  className?: string;
}

export function Tabs({ items, value, onChange, ariaLabel, className }: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const moveFocus = (from: string, direction: 1 | -1) => {
    const enabled = items.filter(item => item.disabled !== true);
    if (enabled.length === 0) return;
    const currentIndex = enabled.findIndex(item => item.id === from);
    const nextItem = enabled[(currentIndex + direction + enabled.length) % enabled.length];
    if (nextItem === undefined) return;
    onChange(nextItem.id);
    listRef.current?.querySelector<HTMLButtonElement>(`[data-tab-id="${nextItem.id}"]`)?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="horizontal"
      className={cn('lc-tabs', className)}
      onKeyDown={event => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          moveFocus(value, 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          moveFocus(value, -1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          const first = items.find(item => item.disabled !== true);
          if (first !== undefined) onChange(first.id);
        } else if (event.key === 'End') {
          event.preventDefault();
          const last = [...items].reverse().find(item => item.disabled !== true);
          if (last !== undefined) onChange(last.id);
        }
      }}
    >
      {items.map(item => {
        const selected = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            data-tab-id={item.id}
            id={`tab-${item.id}`}
            aria-selected={selected}
            aria-controls={`tabpanel-${item.id}`}
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            className={cn('lc-tab', selected && 'lc-tab--selected')}
            onClick={() => onChange(item.id)}
          >
            {item.icon === undefined ? null : <Icon name={item.icon} size="sm" />}
            <span>{item.label}</span>
            {item.badge === undefined ? null : <span className="lc-tab__badge">{item.badge}</span>}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  id,
  value,
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { id: string; value: string; children: ReactNode }) {
  if (id !== value) return null;
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${id}`}
      aria-labelledby={`tab-${id}`}
      tabIndex={0}
      className={cn('lc-tabpanel', className)}
      {...rest}
    >
      {children}
    </div>
  );
}

export interface NavItemRecord {
  id: string;
  label: string;
  icon?: IconName;
  href?: string;
  badge?: ReactNode;
  disabled?: boolean;
  sections?: Array<NavItemRecord>;
}

export interface SidebarProps {
  items: Array<NavItemRecord>;
  activeId: string;
  onSelect: (id: string) => void;
  label: string;
  header?: ReactNode;
  footer?: ReactNode;
  collapsed?: boolean;
  className?: string;
}

export function Sidebar({
  items,
  activeId,
  onSelect,
  label,
  header,
  footer,
  collapsed = false,
  className,
}: SidebarProps) {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (id: string): void => {
    setOpenGroups(current => ({ ...current, [id]: !groupIsOpen(current, id) }));
  };

  return (
    <nav
      aria-label={label}
      data-collapsed={collapsed ? 'true' : undefined}
      className={cn('lc-sidebar', collapsed && 'lc-sidebar--collapsed', className)}
    >
      {header === undefined ? null : <div className="lc-sidebar__header">{header}</div>}
      <ul className="lc-nav">
        {items.map(item => (
          <NavEntry
            key={item.id}
            item={item}
            activeId={activeId}
            onSelect={onSelect}
            collapsed={collapsed}
            openGroups={openGroups}
            onToggleGroup={toggleGroup}
          />
        ))}
      </ul>
      {footer === undefined ? null : <div className="lc-sidebar__footer">{footer}</div>}
    </nav>
  );
}

function groupIsOpen(openGroups: Record<string, boolean>, id: string): boolean {
  return openGroups[id] !== false;
}

function NavEntry({
  item,
  activeId,
  onSelect,
  collapsed,
  openGroups,
  onToggleGroup,
}: {
  item: NavItemRecord;
  activeId: string;
  onSelect: (id: string) => void;
  collapsed: boolean;
  openGroups: Record<string, boolean>;
  onToggleGroup: (id: string) => void;
}) {
  if (item.sections !== undefined) {
    const open = groupIsOpen(openGroups, item.id);
    const listId = `lc-nav-group-${item.id}`;
    if (collapsed) {
      return (
        <li className="lc-nav__group" role="presentation">
          <span className="lc-sr-only">{item.label}</span>
          <ul className="lc-nav">
            {item.sections.map(child => (
              <NavEntry
                key={child.id}
                item={child}
                activeId={activeId}
                onSelect={onSelect}
                collapsed={collapsed}
                openGroups={openGroups}
                onToggleGroup={onToggleGroup}
              />
            ))}
          </ul>
        </li>
      );
    }
    return (
      <li className="lc-nav__group">
        <button
          type="button"
          className="lc-nav__group-label"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onClick={() => onToggleGroup(item.id)}
        >
          <span className="lc-nav__group-name">{item.label}</span>
          <span className="lc-nav__group-chevron">
            <Icon name="chevronDown" size="xs" />
          </span>
        </button>
        {open ? (
          <ul className="lc-nav" id={listId}>
            {item.sections.map(child => (
              <NavEntry
                key={child.id}
                item={child}
                activeId={activeId}
                onSelect={onSelect}
                collapsed={collapsed}
                openGroups={openGroups}
                onToggleGroup={onToggleGroup}
              />
            ))}
          </ul>
        ) : null}
      </li>
    );
  }

  const active = item.id === activeId;
  const anchor = item.href ?? '#';
  return (
    <li>
      <a
        href={item.disabled === true ? undefined : anchor}
        aria-disabled={item.disabled === true ? true : undefined}
        aria-current={active ? 'page' : undefined}
        className={cn('lc-nav__item', active && 'lc-nav__item--active')}
        title={collapsed ? item.label : undefined}
        onClick={event => {
          event.preventDefault();
          if (item.disabled === true) {
            return;
          }
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
            return;
          }
          onSelect(item.id);
        }}
      >
        {item.icon === undefined ? null : <Icon name={item.icon} size="sm" />}
        {collapsed ? null : <span className="lc-nav__label">{item.label}</span>}
        {collapsed || item.badge === undefined ? null : (
          <span className="lc-nav__badge">{item.badge}</span>
        )}
        <span aria-hidden="true" className="lc-nav__indicator" />
      </a>
    </li>
  );
}

export interface StepItem {
  id: string;
  label: string;
  description?: string;
}

export function StepNav({
  steps,
  currentId,
  onSelect,
  label,
}: {
  steps: Array<StepItem>;
  currentId: string;
  onSelect?: (id: string) => void;
  label: string;
}) {
  const currentIndex = steps.findIndex(step => step.id === currentId);
  return (
    <ol className="lc-steps" aria-label={label}>
      {steps.map((step, index) => {
        const state =
          index === currentIndex ? 'current' : index < currentIndex ? 'done' : 'upcoming';
        return (
          <li key={step.id} className={`lc-step lc-step--${state}`}>
            {onSelect === undefined ? (
              <span className="lc-step__button">{step.label}</span>
            ) : (
              <button
                type="button"
                className="lc-step__button"
                aria-current={state === 'current' ? 'step' : undefined}
                onClick={() => onSelect(step.id)}
              >
                {step.label}
              </button>
            )}
            {step.description === undefined ? null : (
              <span className="lc-step__description">{step.description}</span>
            )}
          </li>
        );
      })}
    </ol>
  );
}

export interface CommandItem {
  id: string;
  label: string;
  group?: string;
  icon?: IconName;
  shortcut?: Array<string>;
  keywords?: Array<string>;
  disabled?: boolean;
}

export function CommandPalette({
  open,
  items,
  onClose,
  onSelect,
  placeholder = 'Search commands',
}: {
  open: boolean;
  items: Array<CommandItem>;
  onClose: () => void;
  onSelect: (id: string) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);

  const matched = items.filter(item => {
    const needle = query.trim().toLowerCase();
    if (needle.length === 0) return true;
    return (
      item.label.toLowerCase().includes(needle) ||
      (item.keywords ?? []).some(keyword => keyword.toLowerCase().includes(needle))
    );
  });
  const enabledIndexes = matched
    .map((item, index) => (item.disabled === true ? null : index))
    .filter((index): index is number => index !== null);
  const safeHighlight = Math.min(highlight, Math.max(enabledIndexes.length - 1, 0));

  if (!open) return null;

  const run = (index: number | undefined) => {
    if (index === undefined) return;
    const item = matched[index];
    if (item !== undefined) onSelect(item.id);
  };

  return (
    <div
      className="lc-backdrop lc-backdrop--top"
      onPointerDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-modal="true" aria-label="Command palette" className="lc-command">
        <input
          autoFocus
          className="lc-command__input"
          role="combobox"
          aria-expanded="true"
          aria-controls="command-results"
          aria-activedescendant={`command-${matched[safeHighlight]?.id ?? 'none'}`}
          placeholder={placeholder}
          value={query}
          onChange={event => {
            setQuery(event.target.value);
            setHighlight(0);
          }}
          onKeyDown={event => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onClose();
            } else if (event.key === 'ArrowDown') {
              event.preventDefault();
              setHighlight(current => Math.min(current + 1, enabledIndexes.length - 1));
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setHighlight(current => Math.max(current - 1, 0));
            } else if (event.key === 'Enter') {
              event.preventDefault();
              run(enabledIndexes[safeHighlight]);
            }
          }}
        />
        <ul id="command-results" role="listbox" aria-label="Commands" className="lc-command__list">
          {matched.length === 0 ? (
            <li className="lc-command__empty">No matching commands</li>
          ) : null}
          {matched.map((item, index) => {
            const selected = enabledIndexes[safeHighlight] === index;
            return (
              <li key={item.id} role="option" aria-selected={selected} id={`command-${item.id}`}>
                <button
                  type="button"
                  className={cn('lc-command__item', selected && 'lc-command__item--active')}
                  disabled={item.disabled}
                  onClick={() => {
                    if (item.disabled === true) return;
                    onSelect(item.id);
                  }}
                >
                  {item.icon === undefined ? null : <Icon name={item.icon} size="sm" />}
                  <span className="lc-command__label">{item.label}</span>
                  {item.shortcut === undefined ? null : (
                    <span className="lc-command__shortcut">
                      {item.shortcut.map(key => (
                        <kbd key={key}>{key}</kbd>
                      ))}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export function MobileNavigation({
  open,
  label,
  onClose,
  children,
}: {
  open: boolean;
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  if (!open) return null;
  return (
    <div className="lc-mobile-nav" role="dialog" aria-modal="true" aria-label={label}>
      <div className="lc-mobile-nav__bar">
        <button
          type="button"
          className="lc-button lc-button--ghost lc-button--sm"
          onClick={onClose}
        >
          Close
        </button>
      </div>
      <div className="lc-mobile-nav__body">{children}</div>
    </div>
  );
}
